import EditableDataTable, { ITableRowData } from "@/manager/components/editable-data-table/editable-data-table";
import Page from "@/manager/components/page/page";
import ConfirmationPopup from "@/shared/components/confirmation-popup/confirmation-popup";
import { getAllData, postMultiple } from "@/shared/utils/api";
import useHttp from "@/shared/hooks/use-http";
import { usePrompt } from "@/shared/utils/use-prompt";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "@/shared/context/auth-context";

const TranslationListDetails = () => {
  const params = useParams();
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { flag, setFlag, next } = usePrompt(isDirty);
  const [handleForceSave, setHandleForceSave] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [options, setOptions] = useState<ITableRowData[]>([]);

  const { sendRequest: getTranslationListData, status: getTranslationListStatus, data: getTableDataResult, error: getTableDataError } = useHttp(getAllData, false, authCtx.token, "translationList", "platform");

  const { 
    sendRequest: postTranslationListData,
    status: postTranslationListStatus,
    error: postTranslationListError
  } = useHttp(postMultiple, false, authCtx.token, "translationList", "platform");

  useEffect(() => {
    if (params.id) {
      getTranslationListData(`translation_list/${params.id}`);
    }
  }, [params.id]);

  useEffect(() => {
    if (getTranslationListStatus === "completed" && !getTableDataError && getTableDataResult) {
      setName(getTableDataResult.name);
      const transformedOptions = getTableDataResult.fields.map((field: any) => ({ // eslint-disable-line
        id: field.id,
        source_value: field.source_value,
        target_value: field.target_value
      }));
      setOptions(transformedOptions);
    }
  }, [getTranslationListStatus, getTableDataResult, getTableDataError]);

  const handleSave = (data: ITableRowData[]) => {
    setIsDirty(false);
    setHandleForceSave(false);
    postTranslationListData(
      `translation_list/${params.id}`,
      {
        id: params.id,
        name,
        fields: data
      }
    );
  }

  useEffect(() => {
    if (postTranslationListStatus === "completed" && !postTranslationListError) {
      navigate('/lists#translation-lists');
    }
  }, [postTranslationListStatus, postTranslationListError]);

  return <Page title={{ 
    text: name,
    placeholder: "Translation List",
    tooltip: "Create and manage translation lists. A translation list is a list of source values and their corresponding target values, which can be used for translating data in the platform.",
    onTitleEdited: (newName: string) => {
      setIsDirty(true);
      setName(newName)
    }}}>
     <div className="translationListDetails">
      <EditableDataTable
        defaultSortCol="source_value"
        columns={[{
          key: "source_value",
          displayName: "Source Value",
          dataType: {
            type: "text",
          },
        }, {
          key: "target_value",
          displayName: "Target Value",
          dataType: {
            type: "text",
          },
        }]}
        data={options}
        onSaveClicked={handleSave}
        handleDataChange={(isDirty: boolean) => {
          setIsDirty(isDirty);
        }}
        forceSave={handleForceSave}
        setForceSave={setHandleForceSave}
      />
      {flag && <ConfirmationPopup show={flag} handleSaveClick={() => {
        setIsDirty(false);
        setHandleForceSave(true);
        next();
      }}
      handlePopupClosed={() => {
        setFlag(false);
      }}
      handleContinue={() => { next(); setFlag(false); }} />
      }
    </div>
  </Page>
};

export default TranslationListDetails;